import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LinearProgress from '@material-ui/core/LinearProgress';
import UnifiedBasics from './UnifiedBasics';
import UnifiedNotes from './UnifiedNotes';
import UnifiedStatusNotes from './UnifiedStatusNotes';
import UnifiedServices from './UnifiedServices';
import UnifiedFinancial from './UnifiedFinancial';
import UnifiedCommunications from './UnifiedCommunications';
import UnifiedCustomer from './UnifiedCustomer';
import UnifiedVehicle from './UnifiedVehicle/UnifiedVehicle';
import UnifiedHealthCheckDetails from './UnifiedHealthCheckDetails';
import UnifiedVehiclePhotos from './UnifiedVehicle/UnifiedVehiclePhotos';
import dataProvider from '../../dataProvider';
import UnifiedCustomerAdditionalContacts from './UnifiedCustomerAdditionalContacts';
import { convertPhoneFormat, getZ3PConfigValue } from '../../helper';
import UnifiedPayment from './UnifiedPayment';
import UnifiedViewTopbar from './UnifiedViewTopbar';
import UnifiedCustomerDetails from './UnifiedCustomerDetails';
import './ScheduleWork.css';
import IcTriangle from './icons/ic-eva_alert-triangle.svg';
import ServiceNotificationCard from './ServiceNotificationCard'

const useStyles = makeStyles((theme) => ({
  paperBase: {
    padding: 24,
    marginBottom: 24,
    boxShadow: '0px 12px 24px -4px #919EAB1F',
    borderRadius: 16,
    position: 'relative',
  },
  paperBase2: {
    padding: 10,
    margin: 10,
    boxShadow: '0 0 15px 0 rgb(53 64 82 / 25%)',
    minHeight: '85%',
  },
  panfixed: {
    position: 'fixed',
    width: '26.5%',
    right: 20,
    top: 64,
    [theme.breakpoints.down('lg')]: {
      top: 50,
      right: 'auto',
      width: 'auto',
      position: 'unset',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1),
    },
  },
  row: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  basicsAndVehicle: {
    // Improve print previews and PDFs
    '@media print': {
      maxWidth: '50% !important',
    },
  },
  mainBox: {
    paddingRight: 12,
    paddingLeft: 12,
  },
}));

const formatAdditionalPhoneNumberList = (phoneNumberList = []) => {
  const formattedList = [];
  if (phoneNumberList?.length > 0) {
    // eslint-disable-next-line array-callback-return
    phoneNumberList.map((phoneNumber) => {
      formattedList.push({
        phoneNumber: convertPhoneFormat(phoneNumber.replace(/[^0-9]/g, '')),
        isEditing: false,
        loading: false,
        cancelLoading: false,
      });
    });
  }
  return formattedList;
};

const UnifiedRepairOrder = (props) => {
  const { record } = props;
  const {
    customer,
    vehicle,
    inspection_results_summary,
    health_check_url,
    hc_is_complete,
    date,
    id,
    st_order_id,
    tracking_url,
    product_sizes,
    product_details,
    st_company_name,
    additional_phone_numbers,
    services,
    source,
  } = record;
  const classes = useStyles();
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  const [customerRecord, setCustomerRecord] = useState(customer);
  const [vehicleRecord, setVehicleRecord] = useState(vehicle);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [filenames, setFilenames] = useState([]);
  const [additionalPhoneNumbers, setAdditionalPhoneNumbers] = useState(
    formatAdditionalPhoneNumberList(additional_phone_numbers),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [healthCheckSummary, setHealthCheckSummary] = useState(
    inspection_results_summary,
  );
  const [hcIsComplete, setHcIsComplete] = useState(hc_is_complete);
  const [stOrderId, setStOrderId] = useState(st_order_id);
  const [trackingUrl, setTrackUrl] = useState(tracking_url);
  const [productSizes, setProductSizes] = useState(product_sizes);
  // const tireService = services.find(
  //   (service) => service.display_name === 'Tire Installation',
  // );
  const tireService = services.find(
    (service) =>
      service.display_name === 'Tire Installation' ||
      service.display_name === 'New Tire Installation'
  );
  const tireQty = tireService?.tire_quantity ?? 0;
  const [tireQuantity] = useState(tireQty);
  const [productDetails, setProductDetails] = useState(product_details);
  const [setStCompanyName] = useState(st_company_name);
  const [healthCheckUrl, setHealthCheckUrl] = useState(health_check_url);

  const isUsingServiceReport = getZ3PConfigValue('is_using_service_report');
  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
  const showVehiclePhotos = vehicleRecord?.input_method_type === 'heavy';
  const allowAdditionalUserSmsCommunications = getZ3PConfigValue(
    'allow_additional_user_sms_communications',
  );

  const isPaid =
    record.amount_paid_dollars &&
    record.amount_paid_dollars >= record.total_taxed_price_dollars;
  const isPaymentHeld = record.is_payment_held;
  const isPaidOrHeld = isPaid || isPaymentHeld;
  const isShowImagesAndNote = !!(
    (record.customer_note !== null && record.customer_note.length !== 0) ||
    record.photo_filenames.length
  );

  const fetchRecord = async () => {
    setIsLoading(true);
    try {
      await dataProvider
        .getOne('scheduled-work', {
          id,
        })
        .then(({ data }) => {
          setCustomerRecord(data.customer);
          setVehicleRecord(data.vehicle);
          setHealthCheckSummary(data.inspection_results_summary);
          setHcIsComplete(data.hc_is_complete);
          // map parameter here first
          setStOrderId(data.st_order_id);
          setProductSizes(data.product_sizes);
          setProductDetails(data.product_details);
          setStCompanyName(data.st_company_name);
          setTrackUrl(data.tracking_url);
          setHealthCheckUrl(data.health_check_url);
          setIsLoading(false);
          setAdditionalPhoneNumbers(
            formatAdditionalPhoneNumberList(data.additional_phone_numbers),
          );
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // When creating an RO, the record in props will not have complete data, so we need to fetch the full record
    if (!customerRecord || !healthCheckSummary) {
      fetchRecord();
    }
  }, []);

  useEffect(() => {
    if (vehicleRecord) {
      setFilenames(vehicleRecord.photo_filenames || []);
    }
  }, [vehicleRecord]);

  if (!customerRecord) {
    return <LinearProgress />;
  }

  // This page can have two different component configurations depending on breakpoint

  // For XL screens
  /* if (isXl) { */
  return (
    <Grid className="unifiedWrapper">
      <Grid container spacing={0}>
        <UnifiedViewTopbar {...props} />
      </Grid>
      <Grid container spacing={0}>
        <Grid item xs={12} md={8} xl={8}>
          <Box className={classes.mainBox}>
            <Paper className={classes.paperBase}>
              <UnifiedBasics {...props} />
            </Paper>
            {record.status !== 'finished' && (
              <ServiceNotificationCard service={services} />
            )}
            <Paper className={classes.paperBase}>
              <UnifiedServices {...props} isPaidOrHeld={isPaidOrHeld} />
              <UnifiedFinancial {...props} isPaidOrHeld={isPaidOrHeld} />
            </Paper>
            {isShowImagesAndNote && (
              <Paper className={classes.paperBase}>
                <UnifiedCustomerDetails {...record} />
              </Paper>
            )}
            <Paper className={classes.paperBase}>
              <UnifiedCommunications {...record} />
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={12} md={4} xl={4}>
          <Box className={classes.mainBox}>
            <Paper className={classes.paperBase}>
              <UnifiedPayment {...props} isPaidOrHeld={isPaidOrHeld} />
            </Paper>
            <Paper className={classes.paperBase}>
              {/* stOrderId !== null ? ( {{ stOrderId }} ) */}
              <UnifiedCustomer
                customer={customerRecord}
                storderId={stOrderId}
                tireQty= {tireQty}
              />
              {allowAdditionalUserSmsCommunications && (
                <UnifiedCustomerAdditionalContacts
                  additionalContactList={additionalPhoneNumbers}
                  setAdditionalContactList={setAdditionalPhoneNumbers}
                  swId={id}
                  formatAdditionalPhoneNumberList={
                    formatAdditionalPhoneNumberList
                  }
                />
              )}
            </Paper>
            {!isSkippingVehicle &&
              source !== 'Amazon' &&
              source !== 'AmazonMP' && (
                <Paper className={classes.paperBase}>
                  <UnifiedVehicle
                    customer={customerRecord}
                    vehicle={vehicleRecord}
                    swId={record.id}
                    fetchAppointment={fetchRecord}
                    trackUrl={trackingUrl}
                    proSize={productSizes}
                    ProQuantity={tireQuantity}
                    ProDetails={productDetails}
                    source={source}
                  />
                </Paper>
              )}
            {(source === 'Amazon' || source === 'AmazonMP') && (
              <Paper className={classes.paperBase}>
                <UnifiedVehicle
                  customer={customerRecord}
                  vehicle={vehicleRecord}
                  swId={record.id}
                  fetchAppointment={fetchRecord}
                  trackUrl={trackingUrl}
                  proSize={productSizes}
                  ProQuantity={tireQuantity}
                  ProDetails={productDetails}
                  source={source}
                />
              </Paper>
            )}
            {showVehiclePhotos && (
              <Paper
                className={classes.paperBase2}
                style={{ height: 'fit-content' }}
              >
                <UnifiedVehiclePhotos
                  vehicle={vehicleRecord}
                  enlargedImage={enlargedImage}
                  setEnlargedImage={setEnlargedImage}
                  isUploading={isUploading}
                  setIsUploading={setIsUploading}
                  filenames={filenames}
                  setVehicleRecord={setVehicleRecord}
                />
              </Paper>
            )}
            {isUsingServiceReport && healthCheckSummary && (
              <Paper className={classes.paperBase}>
                <UnifiedHealthCheckDetails
                  healthCheckSummary={{
                    ...healthCheckSummary,
                    healthCheckUrl,
                    hcIsComplete,
                    date,
                  }}
                  updating={isLoading}
                  fetchRecord={fetchRecord}
                />
              </Paper>
            )}
            <Paper className={classes.paperBase}>
              <UnifiedNotes {...record} />
            </Paper>
            {record.status == "cancelled" && <Paper className={classes.paperBase}>
              <UnifiedStatusNotes {...record} />
            </Paper>}
            
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );

  // For screens smaller than XL
  /* return (
    <Grid className="unifiedWrapper">
      <Grid container spacing={0}>
        <UnifiedViewTopbar {...props} />
      </Grid>
      <Grid container spacing={0}>
        <Grid item xs={12} md={7} xl={8}>
          <Grid container className={classes.row}>
            <Grid
              item
              xl={12}
              md={showVehiclePhotos ? 6 : 12}
              className={classes.basicsAndVehicle}
            >
              <Paper className={classes.paperBase}>
                <UnifiedBasics {...props} />
              </Paper>
              <Paper className={classes.paperBase}>
                <UnifiedServices {...props} isPaidOrHeld={isPaidOrHeld} />
                <UnifiedFinancial {...props} isPaidOrHeld={isPaidOrHeld} />
              </Paper>
              <Paper className={classes.paperBase}>
                <UnifiedCommunications {...record} />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={5} xl={4}>
          <Box>
            <Paper className={classes.paperBase}>
              <UnifiedPayment {...props} isPaidOrHeld={isPaidOrHeld} />
            </Paper>
            <Grid item xl={12} className={classes.basicsAndVehicle}>
              <Paper className={classes.paperBase}>
                <UnifiedCustomer customer={customerRecord} storderId={stOrderId} />
                {allowAdditionalUserSmsCommunications && (
                  <UnifiedCustomerAdditionalContacts
                    additionalContactList={additionalPhoneNumbers}
                    setAdditionalContactList={setAdditionalPhoneNumbers}
                    swId={id}
                    formatAdditionalPhoneNumberList={
                      formatAdditionalPhoneNumberList
                    }
                  />
                )}
              </Paper>
            </Grid>
            {!isSkippingVehicle && (
              <Grid item xl={12} className={classes.basicsAndVehicle}>
                <Paper className={classes.paperBase}>
                  <UnifiedVehicle
                    customer={customerRecord}
                    vehicle={vehicleRecord}
                    swId={record.id}
                    fetchAppointment={fetchRecord}
                    trackUrl={trackingUrl}
                    proSize={productSizes}
                    ProQuantity={tireQuantity}
                    ProDetails={productDetails}
                  />
                </Paper>
              </Grid>
            )}
            {showVehiclePhotos && (
              <Grid item xl={12} className={classes.basicsAndVehicle}>
                <Paper
                  className={classes.paperBase2}
                  style={{ height: 'fit-content' }}
                >
                  <UnifiedVehiclePhotos
                    vehicle={vehicleRecord}
                    enlargedImage={enlargedImage}
                    setEnlargedImage={setEnlargedImage}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    filenames={filenames}
                    setVehicleRecord={setVehicleRecord}
                  />
                </Paper>
              </Grid>
            )}
            {isUsingServiceReport && healthCheckSummary && (
              <Grid item xl={12} className={classes.basicsAndVehicle}>
                <Paper className={classes.paperBase}>
                  <UnifiedHealthCheckDetails
                    healthCheckSummary={{
                      ...healthCheckSummary,
                      healthCheckUrl,
                      hcIsComplete,
                      date,
                    }}
                    updating={isLoading}
                    fetchRecord={fetchRecord}
                  />
                </Paper>
              </Grid>
            )}
            <Grid item xl={12} className={classes.basicsAndVehicle}>
              <Paper className={classes.paperBase}>
                <UnifiedNotes {...record} />
              </Paper>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  ); */
};

export default UnifiedRepairOrder;
